import { useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowLeft,
  Plus,
  Copy,
  Check,
  Trash2,
  AlertCircle,
  Code2,
  Globe,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import {
  useOAuthClients,
  useRegisterClient,
  useDeleteClient,
  type OAuthClient,
} from '@/hooks/use-oauth';
import { toast } from 'sonner';

const ALL_SCOPES = [
  { value: 'strategies:read', label: 'Read strategies' },
  { value: 'strategies:write', label: 'Create & edit strategies' },
  { value: 'backtests:run', label: 'Run backtests' },
  { value: 'bots:read', label: 'Read bots' },
  { value: 'bots:write', label: 'Create & manage bots' },
  { value: 'accounts:read', label: 'Read accounts & portfolio' },
  { value: 'analysis:run', label: 'Upload & analyze trades' },
];

export default function DeveloperPage() {
  const { data: clients, isLoading } = useOAuthClients();
  const registerMutation = useRegisterClient();
  const deleteMutation = useDeleteClient();

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(ALL_SCOPES.map((s) => s.value));
  const [createdClient, setCreatedClient] = useState<{ client_id: string; client_secret: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OAuthClient | null>(null);

  const handleCreate = async () => {
    if (!name.trim() || !redirectUri.trim()) return;
    try {
      const result = await registerMutation.mutateAsync({
        name: name.trim(),
        redirect_uris: [redirectUri.trim()],
        scopes: selectedScopes,
      });
      setCreatedClient({
        client_id: result.client_id,
        client_secret: result.client_secret,
      });
      setName('');
      setRedirectUri('');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to register app');
    }
  };

  const handleCopy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('OAuth app deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete app');
    }
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <Link
        to="/settings/api-keys"
        className="inline-flex items-center gap-1.5 text-[11px] text-ink-3 hover:text-ink transition-colors"
      >
        <ArrowLeft size={12} /> API Keys
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold">Developer</h1>
          <p className="text-xs text-ink-3 mt-0.5">
            Register OAuth apps to let other users connect to ForgeAlpha through your app
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus size={14} /> New App
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : clients && clients.length > 0 ? (
        <div className="space-y-2">
          {clients.map((c) => (
            <div
              key={c.id}
              className="glass-sm rounded-lg p-4 flex items-center justify-between gap-4"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Code2 size={14} className="text-blue flex-shrink-0" />
                  <p className="text-sm font-bold">{c.name}</p>
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <code className="text-[10px] font-mono text-ink-3 bg-surface px-1.5 py-0.5 rounded">
                    {c.client_id}
                  </code>
                  {c.website_url && (
                    <span className="text-[10px] text-ink-4 flex items-center gap-1">
                      <Globe size={9} /> {c.website_url}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {c.redirect_uris.map((uri) => (
                    <span
                      key={uri}
                      className="text-[9px] font-mono px-1.5 py-0.5 bg-surface rounded text-ink-4"
                    >
                      {uri}
                    </span>
                  ))}
                </div>
              </div>
              <Button variant="danger" size="sm" onClick={() => setDeleteTarget(c)}>
                <Trash2 size={12} />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass-sm rounded-lg p-8 text-center">
          <Code2 size={32} className="mx-auto text-ink-4 mb-3" />
          <p className="text-sm font-semibold text-ink-3">No OAuth apps registered</p>
          <p className="text-[11px] text-ink-4 mt-1">
            Register an app to allow users to connect their ForgeAlpha accounts to your application.
          </p>
        </div>
      )}

      {/* Create modal */}
      <Modal
        open={showCreate && !createdClient}
        onClose={() => { setShowCreate(false); setName(''); setRedirectUri(''); }}
        title="Register OAuth App"
      >
        <div className="space-y-4">
          <Input label="App Name" placeholder="e.g. My Trading Dashboard" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <Input label="Redirect URI" placeholder="https://yourapp.com/callback" value={redirectUri} onChange={(e) => setRedirectUri(e.target.value)} />
          <div>
            <label className="block text-[11px] font-semibold text-ink-3 mb-2">Scopes</label>
            <div className="space-y-1.5">
              {ALL_SCOPES.map((s) => (
                <label key={s.value} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-surface transition-colors cursor-pointer">
                  <input type="checkbox" checked={selectedScopes.includes(s.value)} onChange={() => toggleScope(s.value)} className="accent-orange w-3.5 h-3.5" />
                  <span className="text-xs">{s.label}</span>
                  <code className="text-[9px] font-mono text-ink-4 ml-auto">{s.value}</code>
                </label>
              ))}
            </div>
          </div>
          <Button className="w-full" onClick={handleCreate} disabled={!name.trim() || !redirectUri.trim() || selectedScopes.length === 0 || registerMutation.isPending}>
            {registerMutation.isPending ? <Spinner size="sm" /> : <Code2 size={14} />}
            Register App
          </Button>
        </div>
      </Modal>

      {/* Created credentials modal */}
      <Modal
        open={!!createdClient}
        onClose={() => { setCreatedClient(null); setShowCreate(false); }}
        title="OAuth App Registered"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 bg-amber/5 border border-amber/15 rounded-lg px-3 py-2.5">
            <AlertCircle size={14} className="text-amber flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber/90">
              Copy the client secret now — it won't be shown again.
            </p>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-ink-3 mb-1">Client ID</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] font-mono bg-surface border border-glass-border rounded-lg px-3 py-2 break-all select-all">{createdClient?.client_id}</code>
              <button onClick={() => handleCopy(createdClient?.client_id || '', 'Client ID')} className="flex-shrink-0 w-8 h-8 rounded-lg bg-surface border border-glass-border flex items-center justify-center text-ink-3 hover:text-orange transition-colors cursor-pointer">
                {copied === 'Client ID' ? <Check size={14} className="text-green" /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-ink-3 mb-1">Client Secret</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] font-mono bg-surface border border-glass-border rounded-lg px-3 py-2 break-all select-all">{createdClient?.client_secret}</code>
              <button onClick={() => handleCopy(createdClient?.client_secret || '', 'Client Secret')} className="flex-shrink-0 w-8 h-8 rounded-lg bg-surface border border-glass-border flex items-center justify-center text-ink-3 hover:text-orange transition-colors cursor-pointer">
                {copied === 'Client Secret' ? <Check size={14} className="text-green" /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          <Button variant="ghost" className="w-full" onClick={() => { setCreatedClient(null); setShowCreate(false); }}>
            Done
          </Button>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete OAuth App">
        <div className="space-y-4">
          <p className="text-xs text-ink-2">
            Delete <span className="font-bold">{deleteTarget?.name}</span>? All users who authorized this app will lose access immediately.
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" className="flex-1" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? <Spinner size="sm" /> : <Trash2 size={12} />}
              Delete App
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
